"use client"
import { useState, useRef, useEffect } from "react"
import { MessageSquare, X } from "lucide-react"
import "./style.css"

const ProductCard = ({
  name,
  description,
  price,
}: {
  name: string
  description: string
  price: number
}) => (
  <div className="border p-4 rounded-lg shadow-md bg-white dark:bg-gray-800 w-full">
    <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
      {name}
    </h3>
    <p className="text-gray-700 dark:text-gray-300">{description}</p>
    <p className="text-gray-700 dark:text-gray-300 font-semibold mt-2">
      Price: ${price}
    </p>
  </div>
)

const ChatbotWidget = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<{ role: string; content: string }[]>(
    [
      {
        role: "assistant",
        content: "Hi, I am Clara, your AI assistant! How can I help you today?",
      },
    ]
  )
  const [input, setInput] = useState("")
  const [loading, setLoading] = useState(false)
  const chatEndRef = useRef<HTMLDivElement>(null)

  // To hold the abort controller so we can cancel the fetch
  const abortController = useRef<AbortController | null>(null)

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" })
    }
  }, [messages])

    function getCookieValue(name: string): string | null {
      const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
      return match ? decodeURIComponent(match[1]) : null;
    }


  const handleSend = async () => {
    if (!input.trim()) return

    const userMessage = { role: "user", content: input }
    setMessages((prev) => [...prev, userMessage])
    setInput("")
    setLoading(true)

    // Create a new AbortController instance for this request
    abortController.current = new AbortController()
    const organizationId = getCookieValue("organization_id");
    try {
      const allCookies = document.cookie;
      // console.log("All cookies:", allCookies);

      
      const res = await fetch(`https://app.fernbooks.com/clara_ai/testingChatBot?oid=1dx93xfbgmdjrjax3`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: [...messages, userMessage],
        }),
        signal: abortController.current.signal,
      })

      const reader = res.body?.getReader()
      if (!reader) {
        setLoading(false)
        return
      }

      let assistantMessage = { role: "assistant", content: "" }
      setMessages((prev) => [...prev, assistantMessage])

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        const text = new TextDecoder().decode(value)
        assistantMessage.content += text

        // Update the last assistant message with the new content
        setMessages((prev) => [...prev.slice(0, -1), { ...assistantMessage }])
        await new Promise((r) => setTimeout(r, 20)) // Smooth update
      }
    } catch (err) {
      if ((err as any).name === "AbortError") {
        // Fetch was aborted by user
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: "*Stopped.*" },
        ])
      } else {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: "*Error occurred while fetching response.*",
          },
        ])
        console.error(err)
      }
    } finally {
      setLoading(false)
      abortController.current = null
    }
  }

  const handleStop = () => {
    if (abortController.current) {
      abortController.current.abort()
    }
    setLoading(false)
  }


  function GradientText({
    children,
    className = "",
    colors = ["#40ffaa", "#4079ff", "#40ffaa", "#4079ff", "#40ffaa"],
    animationSpeed = 8,
    showBorder = false,
  }: {
    children: React.ReactNode;
    className?: string;
    colors?: string[];
    animationSpeed?: number;
    showBorder?: boolean;
  }) {
    const gradientStyle = {
      backgroundImage: `linear-gradient(to right, ${colors.join(", ")})`,
      animation: `gradientMove ${animationSpeed}s linear infinite`,
      backgroundSize: "300% 100%",
      WebkitBackgroundClip: "text",
      color: "transparent",
    };

    return (
      <div
        className={`relative inline-block font-semibold ${className}`}
        style={{ cursor: "default" }}
      >
        {showBorder && (
          <div
            className="absolute inset-0 rounded-xl pointer-events-none z-0"
            style={{
              backgroundImage: `linear-gradient(to right, ${colors.join(", ")})`,
              animation: `gradientMove ${animationSpeed}s linear infinite`,
              backgroundSize: "300% 100%",
              filter: "blur(10px)",
              opacity: 0.5,
            }}
          />
        )}
        <span className="relative z-10" style={gradientStyle}>
          {children}
        </span>
      </div>
    );
  }


  // Add this helper function before the return statement:
  function enhanceMessageWithEmojis(content: string) {
    // Remove leading/trailing quotes if present
    let cleaned = content.trim();
    if (cleaned.startsWith('"') && cleaned.endsWith('"')) {
      cleaned = cleaned.slice(1, -1);
    }
    // Remove all \n and \r (escaped newlines)
    cleaned = cleaned.replace(/\\[nr]/g, ' ');
    // Remove all real newlines
    cleaned = cleaned.replace(/[\n\r]+/g, ' ');
    // If the content looks like HTML (contains <h1>, <h2>, <ul>, <ol>, <li>, <p>, <strong>, <em>, <br>, etc.), trust it and return as is
    if (/<\/?(h1|h2|h3|ul|ol|li|p|strong|em|br|span)[^>]*>/i.test(cleaned)) {
      return cleaned;
    }
    cleaned = cleaned.replace(
      /^Hi, I am Clara, your AI assistant! How can I help you today\?/,
      "👋 Hi, I am Clara, your AI assistant! How can I help you today?"
    );
    cleaned = cleaned.replace(/Status: Shipped/g, 'Status: <span class="status-shipped">✅ Shipped</span>');
    cleaned = cleaned.replace(/Status: Delivered/g, 'Status: <span class="status-delivered">📦 Delivered</span>');
    cleaned = cleaned.replace(/Status: Fulfilled/g, 'Status: <span class="status-fulfilled">🟠 Fulfilled</span>');
    cleaned = cleaned.replace(/Status: Canceled/g, 'Status: <span class="status-canceled">❌ Canceled</span>');
    cleaned = cleaned.replace(/Status: Not Fulfilled/g, 'Status: <span class="status-not-fulfilled">⏳ Not Fulfilled</span>');
    if (cleaned.startsWith("Hi,") || cleaned.startsWith("👋")) {
      cleaned += " ✨";
    }
    // Convert any remaining newlines to <br> for plain text
    if (!/<\/?[a-z][^>]*>/i.test(cleaned)) {
      cleaned = cleaned.replace(/\n/g, '<br>');
    }
    return cleaned;
  }


  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="chat-toggle-button"
        aria-label="Toggle Chatbot"
      >
        <MessageSquare style={{ width: "24px", height: "24px" }} />
      </button>

      {/* Chat Widget */}
      {isOpen && (
        <div className="chat-widget">
          {/* Header */}
          <div className="chat-header">
            <span className="title">
              <span role="img" aria-label="robot">
                <img src="https://cdn-icons-png.flaticon.com/128/8345/8345997.png" alt="Robot Icon" style={{ width: "2rem", height: "2rem" }} />
              </span>
              <GradientText
                colors={["#40ffaa", "#4079ff", "#40ffaa", "#4079ff"]}
                animationSpeed={3}
              >
                Fernbooks Assistant
              </GradientText>
            </span>
            <button
              onClick={() => setIsOpen(false)}
              className="close-button"
              aria-label="Close chat"
            >
              &times;
            </button>
          </div>

          {/* Messages */}
          <div className="chat-messages">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`chat-message ${msg.role === "user" ? "user" : "assistant"}`}
                dangerouslySetInnerHTML={{
                  __html: enhanceMessageWithEmojis(msg.content),
                }}
              />
            ))}
            {loading && (
              <div className="typing-indicator">
                <span role="img" aria-label="sparkles">✨</span> Clara is typing...
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Input & Buttons */}
          <div className="input-container">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !loading && handleSend()}
              className="input-field"
              placeholder="Type your message..."
              disabled={loading}
            />
            {/* Send button */}
            <button
              onClick={handleSend}
              className="send-button"
              disabled={loading}
            >
              Send
            </button>
            {/* Stop button */}
            {loading && (
              <button
                onClick={handleStop}
                className="stop-button"
                aria-label="Stop response"
              >
                <X style={{ width: "16px", height: "16px" }} />
              </button>
            )}
          </div>
        </div>
      )}

    </>
  )
}

export default ChatbotWidget